import React from 'react';
import { Mail, Phone, MapPin, Globe, Building2, ShieldCheck } from 'lucide-react';
import LegalLayout from './LegalLayout';

const CARD = "border border-fab-primary-lighter rounded-xl p-4 sm:p-5 bg-fab-elevated/40";

function Row({ icon: Icon, label, value, href, placeholder }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="w-9 h-9 rounded-lg bg-white border border-fab-primary-lighter flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-fab-primary" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">{label}</div>
        {value ? (
          href
            ? <a href={href} className="text-[14px] font-semibold text-fab-text break-words">{value}</a>
            : <div className="text-[14px] font-semibold text-fab-text break-words">{value}</div>
        ) : (
          <div className="text-[13px] italic text-fab-text-muted">{placeholder}</div>
        )}
      </div>
    </div>
  );
}

export default function ContactUs() {
  return (
    <LegalLayout
      title="Contact FABTEXS AI"
      description="Get in touch with FABTEXS AI — the WhatsApp commerce platform for textile wholesalers, operated by FABTEXS – India’s Wholesale Fabric Hub."
      lastUpdated="18 February 2026"
    >
      <p>
        We&apos;d love to hear from you — whether you&apos;re an Indian textile wholesaler
        looking to onboard, an existing customer who needs support, a partner exploring
        an integration, or a researcher with a security disclosure.
      </p>

      <div className={`${CARD} mt-6`} data-testid="contact-company-card">
        <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-3">Company &amp; Brand</div>
        <Row icon={Building2} label="Company" value="FABTEXS – India’s Wholesale Fabric Hub" />
        <Row icon={ShieldCheck} label="Brand" value="FABTEXS AI" />
        <Row icon={Globe} label="Marketing website" value="www.fabtexs.com" href="https://www.fabtexs.com" />
        <Row icon={Globe} label="SaaS application" value="app.fabtexs.com" href="https://app.fabtexs.com" />
      </div>

      <div className={`${CARD} mt-4`} data-testid="contact-reach-card">
        <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-3">How to reach us</div>
        <Row
          icon={Mail}
          label="Support email"
          value="fabtexswholesale@gmail.com"
          href="mailto:fabtexswholesale@gmail.com"
        />
        <Row
          icon={Phone}
          label="Phone"
          value="9082549572"
          placeholder=""
        />
        <Row
          icon={MapPin}
          label="Registered address"
          value="Mumbai, Maharashtra"
          placeholder=""
        />
      </div>

      <h2>Reporting an issue</h2>
      <ul>
        <li>
          <strong>Account &amp; product support</strong>: write to{' '}
          <a href="mailto:fabtexswholesale@gmail.com">fabtexswholesale@gmail.com</a>{' '}
          from the email address registered on your FABTEXS AI account. Typical reply
          time is one business day.
        </li>
        <li>
          <strong>Billing &amp; invoices</strong>: use the same email with the subject line
          &quot;Billing&quot; and include your business name + the last 4 digits of the
          card used for the latest payment.
        </li>
        <li>
          <strong>Data protection / grievance</strong>: write to the Grievance Officer at{' '}
          <a href="mailto:fabtexswholesale@gmail.com">fabtexswholesale@gmail.com</a>{' '}
          with subject line &quot;Grievance — FABTEXS AI&quot;. See the{' '}
          <a href="/privacy-policy">Privacy Policy</a> for the rights you can exercise.
        </li>
        <li>
          <strong>Security disclosure</strong>: please report responsibly to{' '}
          <a href="mailto:fabtexswholesale@gmail.com">fabtexswholesale@gmail.com</a>{' '}
          with subject line &quot;Security&quot; and avoid public disclosure until we
          confirm a fix.
        </li>
      </ul>

      <h2>Partnerships &amp; press</h2>
      <p>
        For integration partnerships (logistics, ERP, payment), reseller arrangements,
        and press queries, email{' '}
        <a href="mailto:fabtexswholesale@gmail.com">fabtexswholesale@gmail.com</a>{' '}
        with subject line &quot;Partnerships&quot; or &quot;Press&quot;.
      </p>

      <h2>For Meta, Razorpay, and other verifiers</h2>
      <p>
        This page is the canonical contact information for FABTEXS AI as required by
        the WhatsApp Embedded Signup review, Meta App Review, and Razorpay merchant
        verification processes. All correspondence should be addressed to{' '}
        <a href="mailto:fabtexswholesale@gmail.com">fabtexswholesale@gmail.com</a>{' '}
        with the relevant reference number in the subject line.
      </p>
    </LegalLayout>
  );
}
